import type { RangePickerValue } from 'product-list/types/Filters'
import {
	type ChangeEventHandler,
	type ComponentProps,
	type KeyboardEventHandler,
	useCallback,
} from 'react'
import { Keyboard } from 'types/keyboard'
import { cx } from 'utils/cx'

import styles from './Range.module.scss'

type Props = Omit<ComponentProps<'input'>, 'onChange'> & {
	readonly range: RangePickerValue
	readonly minLimit?: number
	readonly maxLimit?: number
	readonly value: number
	readonly onChange: (v: number) => void
}

export function Range({
	className,
	name,
	range,
	minLimit,
	maxLimit,
	value,
	onChange,
	...props
}: Props): React.ReactNode {
	const min = minLimit ?? range.min
	const max = maxLimit ?? range.max

	const changeValue = useCallback(
		(newValue: number) => {
			if (newValue <= max && newValue >= min) {
				onChange(newValue)
			}
		},
		[max, min, onChange]
	)

	const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		(e) => changeValue(parseFloat(e.target.value)),
		[changeValue]
	)

	const handleKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
		(e) => {
			if (e.key === Keyboard.ArrowLeft) {
				changeValue(value - 1)
			}
			if (e.key === Keyboard.ArrowRight) {
				changeValue(value + 1)
			}
		},
		[changeValue, value]
	)

	return (
		<input
			className={cx(styles.range, className)}
			name={name}
			type='range'
			min={range.min}
			max={range.max}
			value={value}
			onInput={handleChange}
			onKeyDown={handleKeyDown}
			step={1}
			{...props}
		/>
	)
}
